import * as React from 'react';
import {css, keyframes} from 'styled-components';

// NOTE: load from core once fixed
// import {
//     Gallery,
//     GallerySlide,
//     GalleryContext,
//     NavigationScrollbar
// } from '@volkswagen-onehub/components-core';
import {Gallery, GalleryContext, GallerySlide} from '../gallery-core-v67.18';
import {FadingEdgesNavigation} from './fading-edges-navigation';
import {useMediaQueries} from '../../hooks/useMediaQueries';
import {Fade} from '../../../utils/Fade';

import {styled, Lentils, Breakpoints} from '@volkswagen-onehub/components-core';
import {ArrowActionType, ArrowButtons} from './arrow-buttons';
import {ContentFilterChips, createFilters} from './content-filter';
import {
    getAnimationDelay,
    isElementOutOfViewport,
    SCROLL_RESET_TIMEOUT,
    slidesPerPageConfig
} from './helpers';
import {makeClip} from '../helpers';

type ActionType = Readonly<'panel-item' | ArrowActionType>;

type NavUiInteractionProps = Readonly<{
    actionName: ActionType;
    itemCount?: number;
    itemPosition?: number;
}>;

export interface ContentSliderProps {
    readonly id: string;
    readonly ariaLabel: string;
    readonly previousSlideAriaLabel: string;
    readonly nextSlideAriaLabel: string;
    readonly itemsLabel: string;
    readonly slides: ContentSlide[];
    readonly allChipLabel: string;
    readonly chipAriaLabel: string;
    readonly hideChips?: boolean;
    readonly onSlideChange?: (
        filteredSlides: ContentSlide[],
        index: number
    ) => void;
    readonly onFilterChange?: (filter: string, slidesCount: number) => void;
    readonly onNavUiInteraction: (props: NavUiInteractionProps) => void;
    readonly getSlideXOfYLabel?: (
        slideIndex: number,
        slideAmount: number
    ) => string;
}

export interface ContentSlide {
    readonly key: string;
    readonly category: string;
    readonly element: React.ReactElement;
    readonly ariaLabel: string;
}

const SLIDE_FADE_DURATION = 300;
const SLIDE_FADE_DISTANCE = '24px';

const noop = () => undefined;

const StyledContentSlider = styled.div`
    div[role='group'] {
        // NOTE: force fading edges even when not so many slides in viewport to keep same dimensions
        // NOTE2: the css var was removed in upcoming gallery versions
        // this can be later overrided on slides wrapper via padding & scroll-padding, or a new prop needs to be itroduced for gallery to keep fading edges layout after filltering and "slides <== slides per page"
        --gallery-hasFadingEdges: 1;

        > div {
            // NOTE: we need to resize content to full width of the slide, and not just for mobile, but also due server side render for all breakpoints
            width: 100%;
        }
    }
`;

const switchOpacity = keyframes`
    0% {
        opacity: 0;
    }
    100% {
        opacity: 1;
    }
`;

const StyledElementWrap = styled.div<{
    isHidden: boolean;
}>`
    position: relative;

    ${props =>
        props.isHidden &&
        css`
            animation: ${switchOpacity} ${SCROLL_RESET_TIMEOUT}ms step-end;
        `}
`;

const StyledLentilsWrap = styled.div<{isSingleSlide: boolean}>`
    margin-top: ${props => props.theme.size.dynamic0100};
    display: flex;
    justify-content: center;

    @media (min-width: ${Breakpoints.b560}px) {
        display: none;
    }

    ${({isSingleSlide}) =>
        isSingleSlide &&
        css`
            visibility: hidden;

            div {
                transition: none;
            }
        `}
`;

const StyledTopElementsWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${props => props.theme.size.static250};
    padding: 0 ${props => props.theme.size.grid002};
    margin-bottom: ${props => props.theme.size.dynamic0050};
`;

const StyledHiddenTextForScreenreader = styled.span`
    ${makeClip()}
`;

export const ContentSlider = React.forwardRef<
    HTMLDivElement,
    ContentSliderProps
>((props, ref) => {
    const {
        id,
        ariaLabel,
        previousSlideAriaLabel,
        nextSlideAriaLabel,
        itemsLabel,
        slides,
        allChipLabel,
        chipAriaLabel,
        hideChips,
        onFilterChange = noop,
        onSlideChange = noop,
        onNavUiInteraction,
        getSlideXOfYLabel
    } = props;

    const [disableAnimation, setDisableAnimation] = React.useState(true);
    const [selectedFilter, setSelectedFilter] = React.useState(allChipLabel);
    const filteredSlides = filterSlides(slides, selectedFilter, allChipLabel);
    const updatedFilters = createFilters(slides, allChipLabel);
    const mediaQueries = useMediaQueries();

    const clickFilter = React.useCallback(
        (filter: string) => {
            const slidesCount = filterSlides(
                slides,
                filter,
                allChipLabel
            ).length;
            onFilterChange(filter, slidesCount);
            setSelectedFilter(filter);
            setDisableAnimation(false);
        },
        [allChipLabel, onFilterChange, slides]
    );

    const handleSlideChange = React.useCallback(
        (index: number): void => {
            if (onSlideChange) {
                onSlideChange(
                    filteredSlides,
                    Math.max(index, Math.min(filteredSlides.length - 1, 0))
                );
            }
        },
        [onSlideChange, filteredSlides]
    );

    const topUiElements = (
        <StyledTopElementsWrapper>
            <ContentFilterChips
                chipsAriaLabel={chipAriaLabel}
                filters={updatedFilters}
                selectedFilter={selectedFilter || allChipLabel}
                setSelectedFilter={clickFilter}
            />
            {getSlideXOfYLabel ? (
                <>
                    {/* NOTE: hidden text for AA to announce change of slides also with the label. Can't be part of following SPAN as screenreader reads then only the change in STRONG element */}
                    <StyledHiddenTextForScreenreader aria-live="polite">
                        {getSlideXOfYLabel(
                            filteredSlides.length,
                            slides.length
                        ) +
                            ' ' +
                            itemsLabel}
                    </StyledHiddenTextForScreenreader>
                    <span>
                        <strong>
                            {getSlideXOfYLabel(
                                filteredSlides.length,
                                slides.length
                            )}
                        </strong>
                        {` ${itemsLabel}`}
                    </span>
                </>
            ) : null}
        </StyledTopElementsWrapper>
    );

    const isSingleSlide = filteredSlides.length === 1;

    return (
        <StyledContentSlider ref={ref}>
            <GalleryContext.Consumer>
                {() => (
                    <Gallery
                        id={id}
                        aria-label={ariaLabel}
                        fadingEdges
                        slidesPerPage={slidesPerPageConfig}
                        gutter="static250"
                        topElements={!hideChips && topUiElements}
                        navigationElements={
                            <>
                                <StyledLentilsWrap
                                    isSingleSlide={isSingleSlide}
                                >
                                    <Lentils
                                        onChange={activeIndex => {
                                            onNavUiInteraction({
                                                actionName: 'panel-item',
                                                itemCount:
                                                    filteredSlides.length,
                                                itemPosition: activeIndex + 1
                                            });
                                        }}
                                    />
                                </StyledLentilsWrap>
                                <ArrowButtons
                                    buttonPreviousAriaLabel={
                                        previousSlideAriaLabel
                                    }
                                    buttonNextAriaLabel={nextSlideAriaLabel}
                                    hideChips={hideChips}
                                    onClick={onNavUiInteraction}
                                />
                            </>
                        }
                        handleChange={handleSlideChange}
                    >
                        {filteredSlides.map(
                            (
                                {element, ariaLabel: slideAriaLabel, key},
                                index
                            ) => (
                                <GallerySlide
                                    key={key + selectedFilter}
                                    aria-label={slideAriaLabel}
                                >
                                    <Fade
                                        easing="ease-out"
                                        directionFrom="left"
                                        distance={SLIDE_FADE_DISTANCE}
                                        duration={SLIDE_FADE_DURATION}
                                        delay={getAnimationDelay(
                                            mediaQueries,
                                            index
                                        )}
                                        disableAnimation={
                                            disableAnimation ||
                                            isElementOutOfViewport(
                                                mediaQueries,
                                                index
                                            )
                                        }
                                    >
                                        <StyledElementWrap
                                            isHidden={isElementOutOfViewport(
                                                mediaQueries,
                                                index
                                            )}
                                        >
                                            {element}
                                            <FadingEdgesNavigation
                                                index={index}
                                            />
                                        </StyledElementWrap>
                                    </Fade>
                                </GallerySlide>
                            )
                        )}
                    </Gallery>
                )}
            </GalleryContext.Consumer>
        </StyledContentSlider>
    );
});

function filterSlides(
    slides: ContentSlide[],
    selectedFilter: string,
    allChipLabel: string
): ContentSlide[] {
    return slides.filter(
        ({category}) =>
            !selectedFilter ||
            selectedFilter === allChipLabel ||
            category === selectedFilter
    );
}
